/**
 * Phase 2b — Markdown → React renderer for the public docs surface.
 *
 * Pipeline:
 *   - remark-gfm        GitHub-flavoured markdown (tables, task lists, strikethrough).
 *   - rehype-raw        passthrough of inline HTML (e.g. <callout>, <apiblock>).
 *   - rehype-slug       autogenerates IDs on headings (drives the right-rail TOC).
 *   - rehype-highlight  syntax highlighting via highlight.js (dark theme).
 *
 * Custom component overrides map markdown elements (h2, code, table, etc.)
 * to brand-styled React components.
 */
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSlug from "rehype-slug";
import rehypeHighlight from "rehype-highlight";

import { Callout, CodeBlock, APIBlock, VideoBlock, Diagram, HoverAnchor, LatestRelease } from "./MdxComponents";


// ---------------------------------------------------------------------------
// Component overrides
// ---------------------------------------------------------------------------

function makeHeading(level) {
  // Level 1 = no anchor, no border. Levels 2-3 carry the hover-anchor.
  const Tag = `h${level}`;
  return ({ id, children, ...rest }) => {
    if (level === 1) {
      return (
        <Tag
          {...rest}
          id={id}
          data-testid={id ? `docs-heading-${id}` : undefined}
          className="qt-docs-h1"
        >
          {children}
        </Tag>
      );
    }
    return (
      <Tag
        {...rest}
        id={id}
        data-testid={id ? `docs-heading-${id}` : undefined}
        className={`qt-docs-h${level} group relative`}
      >
        <HoverAnchor id={id} />
        {children}
      </Tag>
    );
  };
}


function CodeRenderer({ className, children, ...rest }) {
  // Inline `code` only — fenced/block code is handled by the `pre` slot below
  // to avoid the `<pre>` / `<div>` inside `<p>` hydration trap.
  return (
    <code
      {...rest}
      className={`qt-docs-inline-code ${className || ""}`}
    >
      {children}
    </code>
  );
}


function PreRenderer({ children, node }) {
  // react-markdown sends fenced code as <pre><code class="language-…">…</code></pre>.
  // We capture the language from the inner <code>'s className AST node and
  // render the styled CodeBlock. The CodeBlock provides the block context so
  // we don't nest a <pre> inside another <pre>.
  const codeNode = (node?.children || []).find((c) => c.tagName === "code");
  const cls = codeNode?.properties?.className || [];
  const langClass = (Array.isArray(cls) ? cls : [cls]).find(
    (c) => typeof c === "string" && c.startsWith("language-")
  );
  const language = langClass ? langClass.slice("language-".length) : null;
  return <CodeBlock language={language}>{children}</CodeBlock>;
}


function TableRenderer({ children }) {
  return (
    <div
      className="my-8 rounded-md overflow-x-auto"
      style={{ border: "1px solid rgba(255,255,255,0.06)" }}
    >
      <table className="w-full text-sm font-body" style={{ borderCollapse: "collapse" }}>
        {children}
      </table>
    </div>
  );
}


function TheadRenderer({ children }) {
  return <thead style={{ background: "#0A1224" }}>{children}</thead>;
}


function ThRenderer({ children }) {
  return (
    <th
      className="px-4 py-3 text-left text-[10px] font-mono font-bold uppercase tracking-[0.18em]"
      style={{ borderBottom: "2px solid var(--qt-cyan)", color: "var(--qt-cyan)" }}
    >
      {children}
    </th>
  );
}


function TdRenderer({ children }) {
  return (
    <td
      className="px-4 py-3 text-sm"
      style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", color: "#8B9BB4" }}
    >
      {children}
    </td>
  );
}


function TrRenderer({ children }) {
  return (
    <tr className="hover:bg-white/[0.04] transition-colors">
      {children}
    </tr>
  );
}


function BlockquoteRenderer({ children }) {
  return (
    <blockquote
      className="my-6 italic"
      style={{
        borderLeft: "4px solid rgba(255,255,255,0.20)",
        paddingLeft: "1rem",
        paddingTop: "0.25rem",
        paddingBottom: "0.25rem",
        color: "#F8FAFC",
        fontFamily: "'Chivo', system-ui, sans-serif",
        fontSize: "1.125rem",
      }}
    >
      {children}
    </blockquote>
  );
}


function LinkRenderer({ href, children, ...rest }) {
  const isExternal = href && /^https?:/i.test(href);
  return (
    <a
      {...rest}
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className="qt-docs-link"
    >
      {children}
    </a>
  );
}


function UlRenderer({ children }) {
  return <ul className="qt-docs-ul">{children}</ul>;
}


function OlRenderer({ children }) {
  return <ol className="qt-docs-ol">{children}</ol>;
}


function ImgRenderer({ src, alt, title }) {
  if (!src) return null;
  return (
    <figure className="my-8">
      <img
        src={src}
        alt={alt || ""}
        loading="lazy"
        className="w-full h-auto rounded-md"
        style={{ border: "1px solid rgba(255,255,255,0.06)" }}
      />
      {title && (
        <figcaption
          className="text-xs font-mono mt-2 text-center"
          style={{ color: "#8B9BB4" }}
        >
          {title}
        </figcaption>
      )}
    </figure>
  );
}


// Custom HTML tags from rehype-raw — react-markdown lowercases them.
function CalloutTag({ type, title, children }) {
  return <Callout type={type} title={title}>{children}</Callout>;
}

function ApiBlockTag({ method, path, children }) {
  return <APIBlock method={method} path={path}>{children}</APIBlock>;
}

function VideoTag({ src, poster, title, children }) {
  if (src) {
    return <VideoBlock src={src} poster={poster} title={title || (typeof children === "string" ? children : null)} />;
  }
  return null;
}

function DiagramTag({ children }) {
  return <Diagram>{children}</Diagram>;
}

function LatestReleaseTag({ count }) {
  return <LatestRelease count={count} />;
}


export default function MarkdownRenderer({ children }) {
  return (
    <div className="qt-docs-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSlug, [rehypeHighlight, { ignoreMissing: true }]]}
        components={{
          h1: makeHeading(1),
          h2: makeHeading(2),
          h3: makeHeading(3),
          h4: makeHeading(4),
          code: CodeRenderer,
          pre: PreRenderer,
          table: TableRenderer,
          thead: TheadRenderer,
          th: ThRenderer,
          td: TdRenderer,
          tr: TrRenderer,
          blockquote: BlockquoteRenderer,
          a: LinkRenderer,
          ul: UlRenderer,
          ol: OlRenderer,
          img: ImgRenderer,
          callout: CalloutTag,
          apiblock: ApiBlockTag,
          video: VideoTag,
          diagram: DiagramTag,
          latestrelease: LatestReleaseTag,
        }}
      >
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}
